//#region src/org-permissions.ts
/**
* Org-level permission constants — the canonical set used by Data Plane
* org-role definitions and the org admin UI. Reconciled with the runtime
* catalog DP shipped before SDK promotion (`cloud/dp/core-api/src/admin/
* permission-catalog.ts`); SDK is now the single source of truth.
*/
const OrgPermission = {
	OrgRead: "org:read",
	OrgManage: "org:manage",
	UsersRead: "users:read",
	UsersManage: "users:manage",
	GroupsRead: "groups:read",
	GroupsManage: "groups:manage",
	SettingsRead: "settings:read",
	SettingsManage: "settings:manage",
	SettingsRolesRead: "settings:roles:read",
	SettingsRolesManage: "settings:roles:manage",
	SettingsIdentityRead: "settings:identity:read",
	SettingsIdentityManage: "settings:identity:manage",
	ChannelsRead: "channels:read",
	ChannelsManage: "channels:manage",
	FixturesAccess: "fixtures:access",
	AuditRead: "audit:read",
	DlqRead: "dlq:read",
	DlqManage: "dlq:manage",
	CustomerChannelRead: "customer-channel:read",
	CustomerChannelManage: "customer-channel:manage",
	ChannelAssignmentRead: "channel-assignment:read",
	ChannelAssignmentManage: "channel-assignment:manage",
	AppPermissionGrantRead: "app-permission-grant:read",
	AppPermissionGrantManage: "app-permission-grant:manage",
	AppChannelBindingRead: "app-channel-binding:read",
	AppChannelBindingManage: "app-channel-binding:manage",
	LlmCredentialsManage: "org:llm-credentials:manage",
	ChannelOfferRead: "channel-offer:read",
	ChannelOfferManage: "channel-offer:manage",
	AccessReviewRead: "access-review:read"
};
/**
* Canonical org-level catalog — drives the DP role admin UI and is asserted
* by the SDK contract test against DP's runtime exports.
*/
const ORG_PERMISSION_CATALOG = [
	{
		permission: "org:read",
		group: "Organization",
		icon: "Settings",
		label: "View organization",
		description: "See organization name, slug, and basic info"
	},
	{
		permission: "org:manage",
		group: "Organization",
		icon: "Settings",
		label: "Manage organization",
		description: "Edit org settings, archive"
	},
	{
		permission: "users:read",
		group: "Users",
		icon: "Users",
		label: "View users",
		description: "See member list and profiles"
	},
	{
		permission: "users:manage",
		group: "Users",
		icon: "Users",
		label: "Manage users",
		description: "Invite, deactivate, change roles"
	},
	{
		permission: "groups:read",
		group: "Groups",
		icon: "UsersRound",
		label: "View groups",
		description: "See group list and membership"
	},
	{
		permission: "groups:manage",
		group: "Groups",
		icon: "UsersRound",
		label: "Manage groups",
		description: "Create, edit, delete groups"
	},
	{
		permission: "settings:read",
		group: "Settings",
		icon: "Sliders",
		label: "View settings",
		description: "See organization configuration"
	},
	{
		permission: "settings:manage",
		group: "Settings",
		icon: "Sliders",
		label: "Manage all settings",
		description: "The organization record, allowed sign-in domains, custom domain and shell branding — and everything the four narrower settings permissions below grant"
	},
	{
		permission: "settings:roles:read",
		group: "Settings",
		icon: "Sliders",
		label: "View roles",
		description: "See role definitions, which users and groups hold them, and which app permissions each role grants"
	},
	{
		permission: "settings:roles:manage",
		group: "Settings",
		icon: "Sliders",
		label: "Manage roles",
		description: "Create, edit and delete roles; assign roles to users and groups; grant app permissions to roles"
	},
	{
		permission: "settings:identity:read",
		group: "Settings",
		icon: "Sliders",
		label: "View identity setup",
		description: "See configured auth providers, SCIM tokens and OAuth clients. Secrets are never returned"
	},
	{
		permission: "settings:identity:manage",
		group: "Settings",
		icon: "Sliders",
		label: "Manage identity setup",
		description: "Configure single sign-on providers, mint and revoke SCIM tokens, regenerate recovery codes, and register OAuth clients"
	},
	{
		permission: "channels:read",
		group: "Release Channels",
		icon: "Radio",
		label: "View channels",
		description: "See channels and versions"
	},
	{
		permission: "channels:manage",
		group: "Release Channels",
		icon: "Radio",
		label: "Manage channels",
		description: "Create channels, assign groups"
	},
	{
		permission: "fixtures:access",
		group: "Apps",
		icon: "AppWindow",
		label: "Access apps",
		description: "Use apps assigned to your role"
	},
	{
		permission: "audit:read",
		group: "Audit",
		icon: "FileText",
		label: "View audit log",
		description: "Access full event history"
	},
	{
		permission: "dlq:read",
		group: "DLQ",
		icon: "Inbox",
		label: "View DLQ messages",
		description: "Inspect dead-letter-queue entries"
	},
	{
		permission: "dlq:manage",
		group: "DLQ",
		icon: "Inbox",
		label: "Manage DLQ messages",
		description: "Replay or discard dead-letter entries"
	},
	{
		permission: "customer-channel:read",
		group: "Customer Channels",
		icon: "Radio",
		label: "View customer channels",
		description: "See customer channels and their ring mappings"
	},
	{
		permission: "customer-channel:manage",
		group: "Customer Channels",
		icon: "Radio",
		label: "Manage customer channels",
		description: "Create, edit, delete customer channels"
	},
	{
		permission: "channel-assignment:read",
		group: "Customer Channels",
		icon: "Radio",
		label: "View channel assignments",
		description: "See which users and groups are assigned to channels"
	},
	{
		permission: "channel-assignment:manage",
		group: "Customer Channels",
		icon: "Radio",
		label: "Manage channel assignments",
		description: "Assign users and groups to customer channels"
	},
	{
		permission: "app-channel-binding:read",
		group: "Customer Channels",
		icon: "Radio",
		label: "View app channel mappings",
		description: "See which channel each app is mapped to"
	},
	{
		permission: "app-channel-binding:manage",
		group: "Customer Channels",
		icon: "Radio",
		label: "Manage app channel mappings",
		description: "Map an app to a channel, or remove its mapping"
	},
	{
		permission: "app-permission-grant:read",
		group: "App Permissions",
		icon: "AppWindow",
		label: "View app permission grants",
		description: "See which users and groups have which app permissions"
	},
	{
		permission: "app-permission-grant:manage",
		group: "App Permissions",
		icon: "AppWindow",
		label: "Manage app permission grants",
		description: "Grant or revoke app permissions to users and groups"
	},
	{
		permission: "org:llm-credentials:manage",
		group: "LLM Credentials",
		icon: "KeyRound",
		label: "Manage LLM credentials",
		description: "Set, test, and remove the organization's OpenAI and Anthropic API keys"
	},
	{
		permission: "channel-offer:read",
		group: "Customer Channels",
		icon: "Radio",
		label: "View private channel offers",
		description: "See the private release channels developers have offered to this organization"
	},
	{
		permission: "channel-offer:manage",
		group: "Customer Channels",
		icon: "Radio",
		label: "Manage private channel offers",
		description: "Accept or decline a private release channel a developer offered to this organization"
	},
	{
		permission: "access-review:read",
		group: "Audit",
		icon: "FileText",
		label: "View access review",
		description: "See the roles, last activity and app use of each person"
	}
];
/** Flat array of every org permission key — convenience for tests + role seeders. */
const ALL_ORG_PERMISSIONS = ORG_PERMISSION_CATALOG.map((e) => e.permission);
/**
* #1130 — UMBRELLA IMPLICATIONS. Holding the key implies holding every key in
* its value set, everywhere `hasPermission()` is consulted.
*
* ## Why this exists
*
* `settings:manage` was ONE slug gating 33 DP routes exclusively: the whole role
* editor, auth-provider CRUD, SCIM-token mint, recovery-code regeneration,
* OAuth-client CRUD and the org record. A user consenting an agent
* `settings:manage` so it could *read the role list* was simultaneously
* consenting it to mint a SCIM bearer and rewrite the org's SSO. The consent
* screen was honest; the user could not say yes to less. The four
* `settings:{roles,identity}:{read,manage}` keys are the blast-radius seam, and
* this table is what keeps every pre-split grant working across the change.
*
* ## ⛔ Why it is applied at CHECK time and not at token-mint time
*
* A delegated token's `orgPermissions` is re-intersected with its `scp` by the
* verifying guards (`narrowPermissionsToScopes` below). An OAuth client
* registered before the split has `allowedScopes: ['settings:manage']`, so the
* narrowed claim is EXACTLY `['settings:manage']` — any expansion performed
* before that intersection is erased by it, and every re-gated route would 403
* that client. Expanding inside `hasPermission` is therefore not a style
* choice: it is the only placement that preserves continuity for already-issued
* grants. It also means guards, the `@RequireAnyOrgPermission` arm, the shell's
* `buildShellPermissions`, the consent screen's `held` flags and the MCP tool
* predicate all inherit it from one line.
*
* ## ⛔ `settings:read` is deliberately NOT implied by `settings:manage`
*
* `hasPermission(['settings:manage'], 'settings:read')` is `false` today — a
* `settings:manage`-only role cannot `GET /v1/admin/org`. Adding that edge would
* WIDEN existing grants, which is the one thing this change may not do.
*
* ## Shape
*
* A `Map`, not an object literal, on purpose: the key is caller-influenced (org
* role definitions are free strings today), and an object-literal lookup on
* `'constructor'` / `'__proto__'` returns an inherited value whose `.has` is
* not a function — a crash in the hot path of every permission check. A `Map`
* has no prototype chain to walk.
*
* Resolution is ONE level and is not transitive: nothing here implies a key
* that itself implies further keys, and `hasPermission` does not chase chains.
*/
const ORG_PERMISSION_IMPLICATIONS = /* @__PURE__ */ new Map([["settings:manage", /* @__PURE__ */ new Set([
	"settings:roles:read",
	"settings:roles:manage",
	"settings:identity:read",
	"settings:identity:manage"
])]]);
//#endregion
export { ALL_ORG_PERMISSIONS, ORG_PERMISSION_CATALOG, ORG_PERMISSION_IMPLICATIONS, OrgPermission };

//# sourceMappingURL=org-permissions.js.map